import { useState } from "react";
import { PageLayout } from "@/components/PageLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Calculator } from "lucide-react";
import { toast } from "sonner";
import { CalculatorSuperCalcCTA } from "@/components/CalculatorSuperCalcCTA";
import { calculatorCTAs } from "@/data/calculatorCTAs";

const H = 6.62607015e-34;
const C = 299792458;
const EV = 1.602176634e-19; // 1 eV in Joules

export default function PhotoelectricEffectCalculator() {
  const [frequency, setFrequency] = useState("");
  const [workFunction, setWorkFunction] = useState("");
  const [result, setResult] = useState<any>(null);

  const calculateKE = () => {
    const f = parseFloat(frequency);
    const phi = parseFloat(workFunction);

    if (isNaN(f) || isNaN(phi)) {
      toast.error("Please enter valid values");
      return;
    }

    const photonEnergy = H * f;
    const photonEnergyEV = photonEnergy / EV;
    const keJoules = photonEnergy - phi * EV;
    const keEV = keJoules / EV;
    
    if (keJoules < 0) {
      toast.error("Photon energy less than work function - no electrons ejected");
      setResult({ photonEnergy: photonEnergyEV, ke: 0, ejected: false });
      return;
    }

    setResult({
      photonEnergy: photonEnergyEV,
      ke: keEV,
      ejected: true,
    });
    toast.success("Calculation complete!");
  };

  const reset = () => {
    setFrequency("");
    setWorkFunction("");
    setResult(null);
  };

  return (
    <PageLayout calculatorName="Photoelectric Effect Calculator" calculatorPath="/calculator/photoelectric-effect">
      <div className="max-w-2xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold mb-2">Photoelectric Effect Calculator</h1>
          <p className="text-muted-foreground">
            Calculate electron ejection from light absorption
          </p>
        </div>

        <Card className="shadow-lg">
          <CardHeader>
            <CardTitle>Formula: KE_max = hf - Φ</CardTitle>
            <CardDescription>
              Einstein's photoelectric equation
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="frequency">Photon Frequency (Hz)</Label>
                <Input
                  id="frequency"
                  type="number"
                  step="any"
                  placeholder="Enter frequency (e.g., 1e15)"
                  value={frequency}
                  onChange={(e) => setFrequency(e.target.value)}
                />
                <p className="text-xs text-muted-foreground">
                  Or use f = c/λ where c = 3×10⁸ m/s
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="workFunction">Work Function Φ (eV)</Label>
                <Input
                  id="workFunction"
                  type="number"
                  step="any"
                  placeholder="Enter work function"
                  value={workFunction}
                  onChange={(e) => setWorkFunction(e.target.value)}
                />
                <p className="text-xs text-muted-foreground">
                  Common metals: Na (2.3), Al (4.1), Cu (4.7), Au (5.1)
                </p>
              </div>
            </div>

            <div className="flex gap-3">
              <Button onClick={calculateKE} className="flex-1">
                <Calculator className="mr-2 h-4 w-4" />
                Calculate
              </Button>
              <Button onClick={reset} variant="outline">
                Reset
              </Button>
            </div>

            {result && (
              <>
                <div className="space-y-3">
                  <Card className="bg-gradient-to-br from-primary/10 to-primary/5 border-primary/20">
                    <CardHeader>
                      <CardTitle className="text-xl">Results</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div>
                        <p className="text-sm text-muted-foreground">Photon Energy</p>
                        <p className="text-2xl font-bold text-primary">
                          {result.photonEnergy.toFixed(4)} eV
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Max Kinetic Energy</p>
                        <p className="text-2xl font-bold text-primary">
                          {result.ke.toFixed(4)} eV
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Electron Ejection</p>
                        <p className={`text-lg font-bold ${result.ejected ? 'text-green-600' : 'text-red-600'}`}>
                          {result.ejected ? "Yes - Electrons ejected" : "No - Insufficient energy"}
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                </div>
                
                <CalculatorSuperCalcCTA config={calculatorCTAs["photoelectric-effect"]} trigger={result.ke} />
              </>
            )}

            <Card className="bg-muted/50">
              <CardHeader>
                <CardTitle className="text-lg">Key Concepts</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm text-muted-foreground">
                <p>• Light behaves as particles (photons)</p>
                <p>• Each photon has energy E = hf</p>
                <p>• Minimum frequency required (threshold)</p>
                <p>• Excess energy becomes kinetic energy</p>
                <p>• Intensity affects number of electrons, not KE</p>
              </CardContent>
            </Card>
          </CardContent>
        </Card>
      </div>
    </PageLayout>
  );
}
